import { useState, useRef, useEffect } from 'react';
import { previousSeason } from '@losan/core';
import { useIdentity } from './contexts/IdentityContext.js';
import { fetchMe, verifyMagicToken, toIdentity, logout } from './auth/authApi.js';
import { useDashboard } from './hooks/useDashboard.js';
import { LoginScreen } from './components/LoginScreen.js';
import { SeasonSelector } from './components/SeasonSelector.js';
import { FilterBar } from './components/FilterBar.js';
import { AgentFilter } from './components/AgentFilter.js';
import { KpiCard } from './components/KpiCard.js';
import { ClientsMap } from './components/ClientsMap.js';
import { BadgesPanel } from './components/BadgesPanel.js';
import { ProductMixPanel } from './components/ProductMixPanel.js';
import { ClientRankingTable } from './components/ClientRankingTable.js';
import { ProductRankingTable } from './components/ProductRankingTable.js';
import { WeeklyEvolutionChart } from './components/WeeklyEvolutionChart.js';
import { ObjetivoSS27Form } from './components/ObjetivoSS27Form.js';
import { ObjetivosSS27Admin } from './components/ObjetivosSS27Admin.js';
import { useExportPdf } from './hooks/useExportPdf.js';
import logoUrl from './assets/losan-logo.png';

type Tab = 'dashboard' | 'objetivo-ss27';

export function App() {
  const { identity, setIdentity } = useIdentity();
  const [tab, setTab] = useState<Tab>('dashboard');
  const [booting, setBooting] = useState(!identity);
  const [authNotice, setAuthNotice] = useState<string | null>(null);

  // On first load: consume a magic-link token if present, otherwise try to
  // rehydrate an existing session. Skipped when an identity was injected by a
  // host portal via mount({ identity }).
  useEffect(() => {
    if (identity) { setBooting(false); return; }
    let alive = true;
    (async () => {
      const url = new URL(window.location.href);
      const token = url.searchParams.get('token');
      const isAuthRoute = url.pathname.replace(/\/+$/, '') === '/auth';
      if (isAuthRoute && token) {
        try {
          const id = await verifyMagicToken(token);
          if (alive) setIdentity(toIdentity(id));
        } catch (err) {
          if (alive) setAuthNotice((err as Error).message);
        } finally {
          window.history.replaceState({}, '', '/');
        }
      } else {
        const me = await fetchMe();
        if (alive && me) setIdentity(toIdentity(me));
      }
      if (alive) setBooting(false);
    })();
    return () => { alive = false; };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  async function onLogout() {
    await logout();
    setIdentity(null);
  }

  if (booting) return <div className="app"><div className="loading">Cargando…</div></div>;
  if (!identity) return <LoginScreen notice={authNotice} />;

  const isAdmin = identity.role === 'admin';
  return (
    <div className="app-shell">
      <nav className="tab-nav no-export">
        <button className={tab === 'dashboard' ? 'active' : ''} onClick={() => setTab('dashboard')}>
          {isAdmin ? 'Panel' : 'Dashboard'}
        </button>
        <button className={tab === 'objetivo-ss27' ? 'active' : ''} onClick={() => setTab('objetivo-ss27')}>
          Objetivo SS27
        </button>
        <button className="tab-logout" onClick={onLogout} title="Cerrar sesión">
          {identity.displayName ? `${identity.displayName} · ` : ''}Salir
        </button>
      </nav>

      {tab === 'dashboard' && <Dashboard agentId={identity.agentId} role={identity.role} />}
      {tab === 'objetivo-ss27' && (
        isAdmin
          ? <ObjetivosSS27Admin />
          : <ObjetivoSS27Form agentId={identity.agentId} agentName={identity.displayName ?? identity.agentId} />
      )}
    </div>
  );
}

function Dashboard({ agentId, role }: { agentId: string; role: 'agent' | 'admin' }) {
  // Filters held in component state. Season is optional on first load — the
  // use case picks the most recent available season; once it answers we move
  // it into state so the agent can switch.
  const [season, setSeason] = useState<string | undefined>(undefined);
  const [line, setLine] = useState<string | undefined>(undefined);
  const [category, setCategory] = useState<string | undefined>(undefined);
  // Admin-only: which agent the admin is viewing. undefined → aggregated view.
  const [viewAgentId, setViewAgentId] = useState<string | undefined>(undefined);
  const exportRef = useRef<HTMLDivElement>(null);
  const { exportPdf, exporting } = useExportPdf();

  const { data, isLoading, isError, error } = useDashboard({
    agentId,
    role,
    period: 'month',
    ...(role === 'admin' && viewAgentId ? { viewAgentId } : {}),
    ...(season ? { season } : {}),
    filters: {
      ...(line ? { line } : {}),
      ...(category ? { category } : {}),
    },
  });

  if (isLoading) return <div className="app"><div className="loading">Cargando…</div></div>;
  if (isError)   return <div className="app"><div className="error">Error: {(error as Error).message}</div></div>;
  if (!data)     return <div className="app"><div className="empty">Sin datos disponibles.</div></div>;

  const {
    agent, kpis, clients, achievements, productMix,
    season: activeSeason, availableSeasons, availableLines, availableCategories,
    clientRanking, productRanking, weeklyEvolution, provenance,
    isAllAgents, viewAgentId: activeViewAgent, availableAgents,
  } = data;

  function handleExport() {
    if (!exportRef.current) return;
    const who = isAllAgents ? 'todos-los-agentes' : agent.name.replace(/\s+/g, '-').toLowerCase();
    void exportPdf(exportRef.current, `dashboard-losan-${who}-${activeSeason}.pdf`);
  }

  return (
    <div className="app" ref={exportRef}>
      <header className="header">
        <div>
          <img className="header-logo" src={logoUrl} alt="LOSAN" />
          <h1>{role === 'admin' ? 'Panel de Administración' : 'Dashboard del Agente'}</h1>
          <div className="agent">
            {isAllAgents
              ? `Vista agregada · ${clients.length.toLocaleString('es-ES')} clientes`
              : `${agent.name} · ${clients.length} ${clients.length === 1 ? 'cliente' : 'clientes'} en cartera`}
          </div>
        </div>
        <div className="header-controls">
          <SeasonSelector
            value={activeSeason}
            options={availableSeasons}
            onChange={(s) => setSeason(s)}
          />
          <button className="export-pdf-btn no-export" onClick={handleExport} disabled={exporting}>
            {exporting ? 'Generando…' : '⬇ PDF'}
          </button>
        </div>
      </header>

      <FilterBar
        lines={availableLines}
        categories={availableCategories}
        selectedLine={line}
        selectedCategory={category}
        onLineChange={setLine}
        onCategoryChange={setCategory}
      >
        {role === 'admin' && (
          <AgentFilter
            agents={availableAgents}
            value={activeViewAgent}
            onChange={(id) => setViewAgentId(id === '__all__' ? undefined : id)}
          />
        )}
      </FilterBar>

      <div className="kpi-grid">
        <KpiCard label="Facturación" kpi={kpis.revenue} format="eur" provenance={provenance.orders} />
        <KpiCard label="Unidades vendidas" kpi={kpis.units} format="int" provenance={provenance.orders} />
        <KpiCard label="Nº de clientes" kpi={kpis.clientCount} format="int" provenance={provenance.orders} />
      </div>

      <WeeklyEvolutionChart
        points={weeklyEvolution}
        provenance={provenance.targets}
        currentSeason={activeSeason}
        previousSeason={previousSeason(activeSeason)}
      />

      <div className="map-section">
        <ClientsMap clients={clients} />
        <div className="legend" aria-label="Leyenda del mapa">
          <span className="legend-item"><span className="legend-dot" style={{ background: 'var(--green)' }} /> Objetivo cumplido</span>
          <span className="legend-item"><span className="legend-dot" style={{ background: 'var(--yellow)' }} /> Bajo objetivo</span>
          <span className="legend-item"><span className="legend-dot" style={{ background: 'var(--red)' }} /> Sin compras este año</span>
        </div>
      </div>

      <ClientRankingTable rows={clientRanking} provenance={provenance.orders} />
      <ProductRankingTable rows={productRanking} provenance={provenance.products} />

      <ProductMixPanel
        mix={productMix}
        provenance={provenance.products}
        currentSeason={activeSeason}
        previousSeasonLabel={previousSeason(activeSeason)}
      />
      <BadgesPanel achievements={achievements} provenance={provenance.gamification} />
    </div>
  );
}
